import React, { useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilterCategoriesQuery } from '@/api/cms/blogFilterCategories';
import { useFeaturedArticleQuery, useKnowledgeHubArticlesQuery } from '@/api/cms/knowledgeHubArticles';
import { fallbackImage } from '@/api/product/mapping';
import { BlogOverviewComponentProps } from '@/components/features/cms/blocks/BlogOverview/register';
import { BlogCard, BlogCardProps, FeaturedArticle } from '@/components/shared';
import { useDamFile } from '@/contexts/DamFileProvider';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { ImageLookupRequestItem } from '@/generated/data.schemas';
import { useLookupFiles } from '@/generated/image';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { formatTranslation } from '@/helpers';
import { formatDate } from '@/helpers/dateHelpers';
import { CmsFilterCategory } from '@/types/filterCategory';
import { MediaSet } from '@/types/media';
import { BlogFilterRow } from './BlogFilterRow';
import styles from './BlogOverview.module.scss';

/** One row on desktop, so the wait reserves a sensible amount rather than a screen of grey */
const skeletonCardCount = 3;

/** Cards that hold the grid open while the articles load, as the product and article lists do */
export const skeletonBlogCards = (count = skeletonCardCount): BlogCardProps[] =>
	Array.from({ length: count }, () => ({ isSkeleton: true, headline: '', readMoreLabel: '' }));

/** Shared with anything that links to a filtered overview, e.g. /resources/knowledge-hub?category=marine */
export const categoryQueryParameter = 'category';

/**
 * CMS BlogOverview component, showing the articles of the given public filter categories.
 *
 * Paging and category filtering are left out until the Ecommerce Search content scope
 * can deliver them, see [@/api/cms/knowledgeHubArticles].
 */
export const BlogOverview: React.FunctionComponent<BlogOverviewComponentProps> = ({
	categories,
	featuredArticle: featuredArticleLink,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);

	// TODO [MB] can blogoverview component be ssr when replaced with ECS
	// Where feasible, the active filter should be reflected in the URL using a query parameter or similar approach.
	// Example: /resources/knowledge-hub?category=marine

	// Articles reference category entries, while the block stores the stable keys,
	// so the picked keys are resolved to the entry ids the article query matches on
	const { data: filterCategories, isLoading: isLoadingCategories } = useFilterCategoriesQuery(settings?.data);

	// The picked category lives in the URL, so a filtered view can be shared and the back
	// button moves between filters. One at a time, as the filter row only ever sets one.
	const [searchParams, setSearchParams] = useSearchParams();
	const categoryParam = searchParams.get(categoryQueryParameter)?.trim() ?? '';

	// An unknown key is ignored rather than emptying the grid, so an old or mistyped link
	// still shows the articles
	const activeCategoryKey = (categories ?? []).some((selection) => selection.key?.trim() === categoryParam)
		? categoryParam
		: '';

	const categoryIds = useMemo(() => {
		const idsByKey = new Map((filterCategories ?? []).map((category) => [category.key, category.id]));
		const ids = new Set<string>();

		for (const selection of categories ?? []) {
			const key = selection.key?.trim();
			// A picked category narrows the grid to that one, otherwise every configured one shows
			if (!key || (activeCategoryKey.length > 0 && key !== activeCategoryKey)) {
				continue;
			}

			const id = idsByKey.get(key);
			if (id) {
				ids.add(id);
			}
		}

		return Array.from(ids);
	}, [categories, filterCategories, activeCategoryKey]);

	// Named in the order the editor picked them, rather than the order the model returns
	const filterRowCategories = useMemo(() => {
		const categoriesByKey = new Map((filterCategories ?? []).map((category) => [category.key, category]));
		const named = new Map<string, CmsFilterCategory>();

		for (const selection of categories ?? []) {
			const key = selection.key?.trim();
			const category = key ? categoriesByKey.get(key) : undefined;
			if (category) {
				named.set(category.key, { key: category.key, name: category.name });
			}
		}

		return Array.from(named.values());
	}, [categories, filterCategories]);

	// Arriving on the page is not the same as choosing to see everything, and the URL cannot
	// tell the two apart, since both leave it without a category
	const [hasUsedFilters, setHasUsedFilters] = useState(false);

	const setActiveCategory = (key: string): void => {
		setHasUsedFilters(true);

		setSearchParams(
			(params) => {
				if (key.length === 0 || params.get(categoryQueryParameter) === key) {
					params.delete(categoryQueryParameter);
				} else {
					params.set(categoryQueryParameter, key);
				}
				return params;
			},
			// The filter row has to stay where it is, instead of the page jumping to the top.
			// preventScrollReset stops the router resetting, while preventScrollTo is what the
			// app itself reads before smooth scrolling on every location change, see App.tsx.
			{ preventScrollReset: true, state: { preventScrollTo: true } },
		);
	};

	const { data: articles, isLoading } = useKnowledgeHubArticlesQuery(settings?.data, { categoryIds });

	// The navigation link only carries the path of the page it points at
	const { data: featuredArticle } = useFeaturedArticleQuery(settings?.data, {
		path: featuredArticleLink?.value?.data?.path?.trim() ?? '',
		categoryIds,
	});

	// The design puts the author next to the meta line, so the avatar is looked up separately
	const featuredAuthorImage = useDamFile(featuredArticle?.author?.profileImage ?? null);
	const featuredImage = useDamFile(featuredArticle?.featuredImage ?? null);

	// One lookup for every article image, instead of one request per card
	const imageRequest = useMemo(
		() => ({
			images: (articles ?? [])
				.map((article) => article.featuredImage)
				.filter((identifier): identifier is ImageLookupRequestItem => !!identifier?.fileId),
		}),
		[articles],
	);

	// TODO [MB] find out if uselookupfiles are redundant because cms already have this logic
	const { data: lookedUpImages } = useLookupFiles(imageRequest, {
		query: { enabled: imageRequest.images.length > 0 },
	});

	const imagesByFileId = useMemo(
		() => new Map((lookedUpImages?.data.images ?? []).map((item) => [item.fileId, item.image as MediaSet])),
		[lookedUpImages],
	);

	if (isLoadingCategories || isLoading) {
		// The block holds its place rather than collapsing, so the page does not jump once the
		// articles arrive. The cards carry their own geometry, so nothing here guesses a height.
		return (
			<div className={'u-container-lg'}>
				<FeaturedArticle className={styles.featured} isSkeleton headline={''} readArticleLabel={''} />
				<ul className={styles.grid}>
					{skeletonBlogCards().map((card, index) => (
						<li className={styles.item} key={index}>
							<BlogCard {...card} />
						</li>
					))}
				</ul>
			</div>
		);
	}

	// A card without somewhere to go is the one thing worth dropping. Whether an article
	// carries a headline is the editor's call, and the CMS query has already left out
	// anything without a category or marked as hidden.
	const visibleArticles = (articles ?? []).filter((article) => article.url);

	const featured = featuredArticle?.headline ? (
		<FeaturedArticle
			className={styles.featured}
			image={featuredImage ?? fallbackImage}
			url={featuredArticle.url}
			labels={featuredArticle.visibleCardLabels}
			headline={featuredArticle.headline}
			description={featuredArticle.shortDescription}
			authorName={featuredArticle.author?.name}
			authorImage={featuredAuthorImage ?? undefined}
			publicationDate={
				featuredArticle.publicationDate
					? formatDate(new Date(featuredArticle.publicationDate), settings?.data.cultureCode ?? '')
					: undefined
			}
			readingTime={featuredArticle.readingTime}
			readArticleLabel={translations?.data.knowledgeHub.readArticle || ''}
			linkAriaLabel={formatTranslation(translations?.data.knowledgeHub.readArticleAriaLabel || '', {
				0: featuredArticle.headline,
			})}
		/>
	) : null;

	const filterRow = (
		<BlogFilterRow
			categories={filterRowCategories}
			isShowingAllArticles={hasUsedFilters && activeCategoryKey.length === 0}
			selectedKeys={activeCategoryKey.length > 0 ? [activeCategoryKey] : []}
			allArticlesLabel={translations?.data.knowledgeHub.allArticles || ''}
			ariaLabel={translations?.data.knowledgeHub.filterByCategory || ''}
			onToggleCategory={setActiveCategory}
			onShowAllArticles={() => setActiveCategory('')}
		/>
	);

	if (visibleArticles.length === 0) {
		return (
			<div className={'u-container-lg'}>
				{featured}
				{filterRow}
				<p className={styles.empty}>{translations?.data.knowledgeHub.noArticles || ''}</p>
			</div>
		);
	}

	return (
		<div className={'u-container-lg'}>
			{featured}
			{filterRow}
			<ul className={styles.grid}>
				{visibleArticles.map((article) => (
					<li className={styles.item} key={article.id}>
						<BlogCard
							image={article.featuredImage ? imagesByFileId.get(article.featuredImage.fileId) : undefined}
							url={article.url}
							headline={article.headline ?? ''}
							description={article.shortDescription}
							labels={article.visibleCardLabels}
							authorName={article.author?.name}
							publicationDate={
								article.publicationDate
									? formatDate(new Date(article.publicationDate), settings?.data.cultureCode ?? '')
									: undefined
							}
							readingTime={article.readingTime}
							readMoreLabel={translations?.data.shared.readMore || ''}
							lazyLoading
						/>
					</li>
				))}
			</ul>
		</div>
	);
};
